import { BaaFieldValue, ExtendedHipaaSetupAction, HipaaSetupAction, HipaaSetupDispatch, HipaaSetupRequest, HipaaSetupResponse } from './hipaa-types';
import { apimCall } from '../../helpers/api';
import { GoFormzServices } from '../../AppSettings';
import { GetStateFunction } from 'gf-types';
import { validateBaaForm } from './validators';
import { HIPAA_SETUP_PATH } from './constants';

/* Action Types */
export const HIPAA_SETUP_INIT_MODAL = 'HIPAA_SETUP_INIT_MODAL';
export const HIPAA_SETUP_NOT_STARTED = 'HIPAA_SETUP_NOT_STARTED';
export const HIPAA_SETUP_GENERIC_ERROR = 'HIPAA_SETUP_GENERIC_ERROR';
export const HIPAA_SETUP_MODAL_OPEN_TOGGLE = 'HIPAA_SETUP_MODAL_OPEN_TOGGLE';

export const HIPAA_SETUP_CAPTURE_STEP_DATA = 'HIPAA_SETUP_CAPTURE_STEP_DATA';
export const HIPAA_SETUP_DISABLE_STEP = 'HIPAA_SETUP_DISABLE_STEP';
export const HIPAA_SETUP_NEXT_STEP = 'HIPAA_SETUP_NEXT_STEP';
export const HIPAA_SETUP_PREVIOUS_STEP = 'HIPAA_SETUP_PREVIOUS_STEP';

export const HIPAA_SETUP_REQUEST = 'HIPAA_SETUP_REQUEST';
export const HIPAA_SETUP_REQUEST_ERROR = 'HIPAA_SETUP_REQUEST_ERROR';
export const HIPAA_SETUP_REQUEST_SUCCESS = 'HIPAA_SETUP_REQUEST_SUCCESS';

export const HIPAA_SETUP_BAA_FILL_FORM = 'HIPAA_FILL_BAA_FORM';
export const HIPAA_SETUP_BAA_IS_VALID = 'HIPAA_SETUP_BAA_IS_VALID';

export const HIPAA_SETUP_SUBMIT_BAA = 'HIPAA_SETUP_SUBMIT_BAA';
export const HIPAA_SETUP_SUBMIT_BAA_SUCCESS = 'HIPAA_SETUP_SUBMIT_BAA_COMPLETE';
export const HIPAA_SETUP_SUBMIT_BAA_ERROR = 'HIPAA_SETUP_SUBMIT_BAA_ERROR';

export const HIPAA_SETUP_PROCESS_BAA = 'HIPAA_SETUP_PROCESSED_BAA';
export const HIPAA_SETUP_PROCESSED_BAA_SUCCESS = 'HIPAA_SETUP_PROCESSED_BAA_SUCCESS';
export const HIPAA_SETUP_PROCESS_BAA_ERROR = 'HIPAA_SETUP_PROCESSED_BAA_ERROR';

/* Actions */

export const initModal = (): HipaaSetupAction => ({ type: HIPAA_SETUP_INIT_MODAL });

export const disableStep = (disabled: boolean): HipaaSetupAction => ({
  type: HIPAA_SETUP_DISABLE_STEP,
  payload: { stepDisabled: disabled }
});

export const nextStep = (currentStepNo: number): HipaaSetupAction => ({
  type: HIPAA_SETUP_NEXT_STEP,
  payload: {
    stepNo: currentStepNo
  }
});

export const captureStepData = (no: number, data?: ExplicitAny): HipaaSetupAction => ({
  type: HIPAA_SETUP_CAPTURE_STEP_DATA,
  payload: {
    stepNo: no,
    stepData: data
  }
});

export const genericError = (message: string): HipaaSetupAction => ({
  type: HIPAA_SETUP_GENERIC_ERROR,
  payload: { errorMessage: message }
});

/* Thunk Actions */

export const getHipaaSetup = () => async (dispatch: HipaaSetupDispatch) => {
  try {
    dispatch({ type: HIPAA_SETUP_REQUEST });

    const { data } = await apimCall<HipaaSetupResponse>(GoFormzServices.AccountService, HIPAA_SETUP_PATH, 'GET');

    // if there's a record that means they've signed the baa so it is considered started
    dispatch({
      type: HIPAA_SETUP_REQUEST_SUCCESS,
      payload: {
        started: true,
        processed: data.processed,
        email: data.email,
        emailVerified: !!data.email,
        baaPdfUrl: data.baaPdfUrl
      }
    });
  } catch (error) {
    switch (error.response?.status as number) {
      case 404:
        dispatch({ type: HIPAA_SETUP_NOT_STARTED });
        break;

      default:
        dispatch({ type: HIPAA_SETUP_REQUEST_ERROR, payload: { errorMessage: error.message } });
        break;
    }
  }
};

export const fillBaaForm = (baaFieldValue: BaaFieldValue) => (dispatch: HipaaSetupDispatch, getState: GetStateFunction) => {
  if (!baaFieldValue) {
    return;
  }

  dispatch({
    type: HIPAA_SETUP_BAA_FILL_FORM,
    payload: {
      baaFieldValue: {
        field: baaFieldValue.field,
        value: baaFieldValue.value
      }
    }
  });

  if (!baaFieldValue.value) {
    dispatch({ type: HIPAA_SETUP_BAA_IS_VALID, payload: { baaIsValid: false } });
    return;
  }

  const { baaForm } = getState().hipaaSetup;
  const baaIsValid = validateBaaForm(baaForm);

  dispatch({ type: HIPAA_SETUP_BAA_IS_VALID, payload: { baaIsValid } });
};

export const saveHipaaBaaForm = () => async (dispatch: HipaaSetupDispatch, getState: GetStateFunction) => {
  try {
    const state = getState();
    const { email, currentStepNo } = state.hipaaSetup;
    const { pdfPath } = state.formeditor;
    const data: HipaaSetupRequest = { email, baaPdfUrl: pdfPath };

    dispatch(disableStep(true));
    dispatch({ type: HIPAA_SETUP_SUBMIT_BAA });
    // save the completed baa form to the hipaa setup table
    await apimCall<void>(GoFormzServices.AccountService, HIPAA_SETUP_PATH, 'POST', data);

    dispatch({ type: HIPAA_SETUP_SUBMIT_BAA_SUCCESS, payload: { baaPdfUrl: data.baaPdfUrl } });
    // move to the next step
    dispatch(nextStep(currentStepNo));
  } catch (error) {
    dispatch({ type: HIPAA_SETUP_SUBMIT_BAA_ERROR, payload: { errorMessage: error.message } });
  }
};

/**
 * Processes the BAA officially by updating our records that we received and process it on our end.
 *
 * @param processingWaitTime The minimum amount of time to wait before enabling the next step.
 */
export const processBaa =
  (processingWaitTime = 2000) =>
  async (dispatch: HipaaSetupDispatch, getState: GetStateFunction) => {
    try {
      const state = getState();
      const { currentStepNo } = state.hipaaSetup;

      dispatch({ type: HIPAA_SETUP_PROCESS_BAA });

      // per req, we want to wait at least 2 secs when processing starts
      const minWaitPromise = new Promise<void>((resolve) => {
        setTimeout(resolve, processingWaitTime);
      });

      const processBaaPromise = apimCall(GoFormzServices.AccountService, HIPAA_SETUP_PATH, 'PUT', {});

      // wait for all of the promises to resolve (a min of 2 seconds)
      await Promise.all([minWaitPromise, processBaaPromise]);

      dispatch({ type: HIPAA_SETUP_PROCESSED_BAA_SUCCESS });
      // move onto the next step once it's processed
      dispatch(nextStep(currentStepNo));
    } catch (error) {
      dispatch({ type: HIPAA_SETUP_PROCESS_BAA_ERROR, payload: { errorMessage: error.message } });
    }
  };

export const toggleHipaaSetupModal = (isOpen: boolean): ExtendedHipaaSetupAction => ({
  type: HIPAA_SETUP_MODAL_OPEN_TOGGLE,
  isModalOpen: isOpen
});
